import { fetchBaseQuery } from '@reduxjs/toolkit/dist/query';
import nodeFetch from 'node-fetch';
import config from './config';
import { ApiError, ProblemDetail } from './apiError';

/**
 * To create authenticated base query for version 2 of the orion API.
 * @protected
 * @returns A function to create base query with authorization header
 */
const v2FetchQuery = () => fetchBaseQuery({
  baseUrl: `${config.apiUrl.orionAPI}/v2`,
  prepareHeaders: (headers, { getState }) => {
    const { token } = getState().entities.auth.me;
    if (token) {
      headers.set('authorization', `Bearer ${token}`);
    }
    return headers;
  },
});

/**
 * Custom base query to handle the custom response and throw appropriate errors if any.
 * @returns {baseQueryFn}
 */
export const crV2BaseQuery = () => async (args, api, extraOptions) => {
  const baseResult = await v2FetchQuery()(args, api, extraOptions);

  /**
   * Handle network error.
   */
  if (baseResult.error?.status === 'FETCH_ERROR') {
    return {
      error: new ApiError(
        0,
        'FETCH_ERROR',
        new ProblemDetail({ detail: baseResult.error.error })
      ),
      meta: baseResult.meta,
    };
  }

  if (!baseResult.meta?.response.ok) {
  /**
   * Handle rest exception message with problem detail.
   */
    const { status, statusText } = baseResult.meta.response;
    const problem = new ProblemDetail({ detail: baseResult.error?.data?.detail ?? baseResult.error?.data });
    /**
     * If there was an API error, convert that to custom ApiError.
     */
    return {
      error: new ApiError(status, statusText, problem),
      meta: baseResult.meta,
    };
  }
  return baseResult;
};

/**
 * Version 2 of the CR fetch api.
 * @public
 * @param {HttpRequestOptions} props Request options.
 * @param {URL} api url
 * @returns {Promise<HttpResponse>}
 */
export const crV2Fetch = (props, api = config.apiUrl.orionAPI) => async (dispatch, getState) => {
  const { token } = getState().entities.auth.me;

  const requestOptions = {
    method: props.method,
    headers: { Authorization: `Bearer ${token}` },
  };

  if (props.body) {
    requestOptions.body = props.body;
  }

  if (!props.multipart) {
    requestOptions.headers = {
      ...requestOptions.headers,
      'Content-Type': 'application/json',
    };
  }
  const response = await nodeFetch(`${api}${props.url}`, requestOptions);
  if (response.ok) {
    return response;
  }
  /**
       * `onError` handle api error.
       */
  throw new ApiError(
    response.status,
    response.statusText,
    new ProblemDetail(await response.json())
  );
};
